import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { RefreshData } from "components/helper";
import Loader from "components/Loader";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useRef, useState } from "react";
import { FaCreditCard, FaFileInvoice, FaMoneyBillWave, FaReceipt } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import PurchaseOrderTemplate from "./PurchaseOrder";
import { formatDisplayDate } from "utils/dateFormat";

const ReferralEntries = () => {
  const [selectedEntryForPrint, setSelectedEntryForPrint] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { clientId } = useParams();
  const { state } = useLocation();
  const contentRef = useRef(null);
  const [{ clientList, usersList }, { refetchClient }] = useKothar();

  useEffect(() => {
    if (!clientList?.length) refetchClient();
  }, [clientList?.length, refetchClient]);
  // Overall summary data (this would come from your API)
  const [overallSummary, setOverallSummary] = useState({
    totalReferrals: 0,
    totalDue: 0,
    totalPaid: 0,
  });

  const getEntries = async () => {
    const res = await axios.get(`${API_URL}/referrals/${id}/clients/${clientId}/entries`);
    return res?.data?.data;
  };

  const {
    data: entriesData,
    refetch,
    isFetching: isEntriesFetching,
  } = useQuery({
    queryKey: ["referral-entries", id],
    queryFn: () => getEntries(),
    enabled: false,
  });

  useEffect(() => {
    if (id && !entriesData) {
      refetch();
    }
  }, [id]);

  // Sample data for demonstration
  useEffect(() => {
    // This would be replaced with actual API calls
    setOverallSummary({
      totalReferrals: 2,
      totalDue: 12500,
      totalPaid: 8900,
    });
  }, []);

  const reactToPrintFn = useReactToPrint({
    contentRef,
  });

  const generatePurchaseOrder = (entry) => {
    setSelectedEntryForPrint(entry);
    // Use setTimeout to ensure the template is rendered before printing
    setTimeout(() => {
      reactToPrintFn();
    }, 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(amount);
  };

  const getClientName = () => {
    const client = clientList?.find((client) => client.id === Number(clientId));
    return client?.name || state?.client?.clientName;
  };

  const getReferralInfo = () => {
    const user = usersList?.find((user) => user.id === Number(state?.referrerUserId));
    return user;
  };

  const getOverallPending = () => {
    return entriesData?.reduce((acc, entry) => acc + entry.pendingAmount, 0);
  };

  const getOverallPaid = () => {
    return entriesData?.reduce((acc, entry) => acc + entry.paidAmount, 0);
  };

  return (
    <div className="p-6 bg-app-surface-muted min-h-screen">
      {isEntriesFetching && <Loader />}
      {/* Overall Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-app-surface rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-app-text-secondary">Overall Referrals</p>
              <p className="text-3xl font-bold text-app-text">{entriesData?.length}</p>
            </div>
            <FaMoneyBillWave className="text-4xl text-blue-500" />
          </div>
        </div>

        <div className="bg-app-surface rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-app-text-secondary">Overall Pending</p>
              <p className="text-3xl font-bold text-app-text">
                {formatCurrency(getOverallPending())}
              </p>
            </div>
            <FaReceipt className="text-4xl text-yellow-500" />
          </div>
        </div>

        <div className="bg-app-surface rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-app-text-secondary">Overall Paid</p>
              <p className="text-3xl font-bold text-app-text">{formatCurrency(getOverallPaid())}</p>
            </div>
            <FaCreditCard className="text-4xl text-green-500" />
          </div>
        </div>
      </div>

      {/* Entries Section */}
      <div className="bg-app-surface rounded-lg shadow-md">
        <div className="flex items-center justify-between p-6 border-b border-app-border">
          <h2 className="flex items-center gap-2 text-2xl font-bold text-app-text">
            <IoArrowBack className="cursor-pointer" onClick={() => navigate(-1)} /> Referral Entries
            of {getClientName()}
            <RefreshData refreshData={() => refetch()} />
          </h2>
        </div>
        <p className="px-6 pb-4 text-sm text-app-text-secondary">
          Referrals recorded before the referral portal. This list is read-only; new referral
          amounts are entered on the client&apos;s account.
        </p>

        {/* Entries Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-app-surface-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider">
                  SN
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider">
                  Description
                </th>{" "}
                <th className="px-6 py-3 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider">
                  Referral Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider">
                  Paid Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider">
                  GST
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider">
                  Paid Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider">
                  Receipts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider">
                  Purchase Order
                </th>
              </tr>
            </thead>
            <tbody className="bg-app-surface divide-y divide-gray-200">
              {entriesData?.length > 0 ? (
                entriesData?.map((entry, index) => (
                  <tr key={entry.id} className="hover:bg-app-surface-hover">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-app-text">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-app-text">
                      {entry.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-app-text">
                      {formatCurrency(entry.referralAmount)}
                    </td>{" "}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-app-text">
                      {formatCurrency(entry.paidAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          entry.gstIncluded
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {entry.gstIncluded ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-app-text">
                      {formatDisplayDate(entry.paidDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-app-text">
                      {entry.receiptNumber ?? "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-app-text">
                      {entry.paymentMethod ?? "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {entry.invoiceGenerated ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Generated
                        </span>
                      ) : (
                        <button
                          onClick={() => generatePurchaseOrder(entry)}
                          className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200"
                        >
                          <FaFileInvoice className="mr-1" />
                          Generate
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-12 whitespace-nowrap text-xxl text-app-text text-center"
                  >
                    No entries found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Purchase Order Template - Hidden on screen, visible when printing */}
      {selectedEntryForPrint && (
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            top: "-9999px",
          }}
        >
          <PurchaseOrderTemplate
            printData={selectedEntryForPrint}
            contentRef={contentRef}
            referralInfo={getReferralInfo()}
          />
        </div>
      )}
    </div>
  );
};

export default ReferralEntries;
